<script lang="ts" setup>
import { z } from 'zod'
import { ref, computed } from 'vue'
import type { PropType } from 'vue'

import { BundleDBModel } from '../helpers/helpers'
import Poster from './Poster.vue'

import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  bundle_id: {
    type: String,
    required: true,
  },
  bundle: {
    type: Object as PropType<z.infer<typeof BundleDBModel>>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'card-click'): void
  (e: 'toggle'): void
}>()

const cardRef = ref<HTMLDivElement | null>(null)

const posterJpgData = computed(() => props.bundle.metadata.poster_jpg_data)
const posterMaskPngData = computed(
  () => props.bundle.metadata.poster_mask_png_data,
)
</script>

<template>
  <div class="card-container">
    <div
      ref="cardRef"
      class="card"
      :class="[
        { 'has-errors': bundle.metadata.error_messages.length > 0, selected },
      ]">
      <div
        v-if="multiSelect"
        class="multi-select-checkbox"
        @click.stop="emit('toggle')">
        <input
          type="checkbox"
          :checked="selected"
          @click.stop />
      </div>
      <button
        class="card-click-target"
        type="button"
        @click="emit('card-click')">
        <span class="sr-only">Select bundle</span>
      </button>
      <p
        v-if="!bundle.metadata.ready"
        class="incomplete">
        Incomplete
      </p>
      <div class="thumbnail-wrapper">
        <Poster
          class="thumbnail"
          :poster-jpg-data="posterJpgData"
          :poster-mask-png-data="posterMaskPngData" />
      </div>

      <div
        class="lower-gradient"
        v-if="bundle.owner_editable.details.title">
        <div class="card-title">{{ bundle.owner_editable.details.title }}</div>
      </div>
      <div class="error-container">
        <ExclamationCircleIcon
          v-if="bundle.metadata.error_messages.length > 0" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 1px solid var(--content-background);
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition-duration: 0.2s;
  justify-content: center;
  max-width: 640px;
  aspect-ratio: 4 / 3;
}

.card:hover {
  box-shadow: 4px 4px 2px 2px rgba(0, 0, 0, 0.1);
  transition-duration: 0.2s;
  border-color: var(--background-reverse);
  background: linear-gradient(
    20deg,
    var(--background-color),
    var(--content-background)
  );
  transform: translateY(-2px);
}

.selected {
  box-shadow: 4px 4px 2px 2px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: var(--background-reverse);
  background-color: var(--content-background);
}

.selected:hover {
  background: var(--content-background);
}

.multi-select-checkbox {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.multi-select-checkbox input {
  pointer-events: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: 2px solid var(--content-background);
  background: rgba(255, 255, 255, 0.9);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.multi-select-checkbox input:checked {
  background: var(--accent-color);
  border-color: var(--accent-color);
}

.multi-select-checkbox input:checked::after {
  content: '';
  width: 8px;
  height: 4px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: translateY(-1px) rotate(-45deg);
}

.card-click-target {
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 1;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.thumbnail-wrapper {
  height: 100%;
}

.thumbnail,
.thumbnail :deep(#lazy-load-poster),
.thumbnail :deep(#poster-svg),
.thumbnail :deep(#poster-image) {
  display: block;
  width: 100%;
  height: 100%;
}

p.incomplete {
  position: absolute;
  z-index: 1;
  color: var(--warning-color);
  top: 30px;
  left: 4px;
  margin: 0.2rem;
}

.image p.info {
  position: absolute;
  top: 2px;
  right: 5px;
}

.image p.incomplete {
  position: absolute;
  top: 2px;
  left: 2px;
}

.image p.error-message {
  position: absolute;
  top: 50%;
  right: 50%;
  cursor: pointer;
}

.lower-gradient {
  position: absolute;
  bottom: 0;
  height: 2.5rem;
  z-index: 2;
  pointer-events: none;
  display: flex;
  align-items: end;
  padding: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  filter: opacity(0.9);
  background: var(--background-color);
}

.card-title {
  font-size: 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  margin: 0;
  white-space: nowrap;
  color: var(--text-color);
  font-family: var(--title-font);
  width: 80%;
}

a {
  text-decoration: none;
  max-height: 220px;
}

.desc-wrapper {
  position: absolute;
  width: 100%;
  bottom: 0;
  padding: 0rem 1rem;
  z-index: 3;
}

.meta-details {
  position: relative;
  display: flex;
  font-size: 0.9rem;
  gap: 0.5rem;
  color: var(--white_4);
}

.error-container {
  position: relative;
  display: flex;
  width: 2rem;
  height: 2rem;
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  pointer-events: none;
}

.error-container svg {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--warning-color);
}

#dialog_margin {
  margin-left: auto;
  margin-right: auto;
}
</style>
